import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ['home', 'about', 'vpn-service', 'contact', 'privacy'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d51vpv1n1516q9jm3ti0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/0e54bcbb-1859-4913-b1dd-2eb5d08795ab.png" 
                alt="FIAGRIL LTDA Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>FIAGRIL LTDA</span>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'about', label: 'About' },
                { id: 'vpn-service', label: 'VPN Service' },
                { id: 'contact', label: 'Contact' },
                { id: 'privacy', label: 'Privacy Policy' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors cursor-pointer ${
                    activeSection === item.id
                      ? isScrolled ? 'text-teal-600' : 'text-teal-400'
                      : isScrolled ? 'text-gray-600 hover:text-teal-600' : 'text-gray-200 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-medium rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Background Image */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://public.readdy.ai/ai/img_res/fd5349ace7ed98712f64eb47f098d9d4.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="space-y-6 mb-12">
            <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight tracking-tight">
              Secure Your<br />
              <span className="bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
                Digital Freedom
              </span><br />
              With FIAGRIL VPN
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Experience ultimate online privacy and security with military-grade encryption technology
            </p>
          </div>

          <button
            onClick={() => scrollToSection('contact')}
            className="inline-flex items-center px-8 py-4 bg-teal-500 text-white text-lg font-semibold rounded-full hover:bg-teal-600 transition-all hover:shadow-lg hover:shadow-teal-500/50 whitespace-nowrap cursor-pointer"
          >
            Get Started Now
            <i className="ri-arrow-right-line ml-2 text-xl"></i>
          </button>

          {/* Device Mockups */}
          <div className="mt-20 relative">
            <div className="flex items-end justify-center space-x-6">
              <div className="w-64 h-40 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg shadow-2xl transform rotate-6 opacity-80"></div>
              <div className="w-80 h-52 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl shadow-2xl transform -translate-y-4 z-10"></div>
              <div className="w-64 h-40 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg shadow-2xl transform -rotate-6 opacity-80"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">About Us</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted VPN Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              FIAGRIL LTDA is dedicated to providing cutting-edge VPN technology that ensures your online privacy and security. Based in Brazil, we serve users worldwide with reliable and fast VPN services.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-4xl text-teal-600"></i>
              </div>
              <p className="text-xs uppercase text-gray-500 tracking-wider mb-2">Email</p>
              <a href="mailto:contact@fiagrilltda.com" className="text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">
                contact@fiagrilltda.com
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-4xl text-teal-600"></i>
              </div>
              <p className="text-xs uppercase text-gray-500 tracking-wider mb-2">Phone</p>
              <a href="tel:+15312284772" className="text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors cursor-pointer">
                +1 531 228 4772
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-4xl text-teal-600"></i>
              </div>
              <p className="text-xs uppercase text-gray-500 tracking-wider mb-2">Location</p>
              <p className="text-lg font-semibold text-gray-900">
                CUIABA, Brazil
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">Features</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose FIAGRIL VPN
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience the ultimate in online security and privacy with our advanced VPN technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Military-Grade Encryption',
                description: 'Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide'
              },
              {
                icon: 'ri-lock-line',
                title: 'No-Log Policy',
                description: 'We never track, collect, or share your private data. Your online activity remains completely private and anonymous'
              },
              {
                icon: 'ri-global-line',
                title: 'Global Server Network',
                description: 'Access content from anywhere with our worldwide network of high-speed servers across multiple countries'
              },
              {
                icon: 'ri-flashlight-line',
                title: 'Lightning Fast Speed',
                description: 'Enjoy seamless browsing, streaming, and downloading with our optimized high-performance servers'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full mb-6">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Security Showcase */}
          <div className="mt-24 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="p-12">
                <p className="text-teal-400 text-sm font-semibold uppercase tracking-wider mb-4">Security First</p>
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                  Military-Grade<br />Encryption
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed mb-8">
                  Our VPN uses the most advanced encryption protocols to ensure your data remains secure and private. Whether you're browsing, streaming, or working remotely, your connection is always protected.
                </p>
                <ul className="space-y-4 mb-8">
                  {[
                    'AES-256 bit encryption standard',
                    'Secure OpenVPN & IKEv2 protocols',
                    'Automatic kill switch protection'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center text-white">
                      <i className="ri-checkbox-circle-fill text-teal-400 text-xl mr-3"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="inline-flex items-center px-6 py-3 border-2 border-teal-400 text-teal-400 font-semibold rounded-lg hover:bg-teal-400 hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>

              <div className="relative h-full min-h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%203d%20visualization%20of%20encrypted%20data%20flow%20with%20glowing%20teal%20and%20cyan%20particles%20forming%20secure%20network%20connections%20featuring%20floating%20geometric%20shapes%20with%20transparency%20effects%20and%20digital%20security%20elements%20on%20a%20dark%20technological%20background%20representing%20vpn%20encryption%20and%20data%20protection&width=800&height=600&seq=security-visual-001&orientation=landscape" 
                  alt="Security Visualization" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get In Touch
            </h2>
            <p className="text-lg text-gray-600">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm disabled:opacity-50"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm disabled:opacity-50"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm disabled:opacity-50"
                  placeholder="How can we help you?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  required
                  maxLength={500}
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none text-sm disabled:opacity-50"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
              </div>

              {formStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm font-medium">
                    <i className="ri-error-warning-line mr-2"></i>
                    Failed to send message. Please try again.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Email Us</h4>
              <a href="mailto:contact@fiagrilltda.com" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                contact@fiagrilltda.com
              </a>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Call Us</h4>
              <a href="tel:+15312284772" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                +1 531 228 4772
              </a>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Visit Us</h4>
              <p className="text-gray-600">
                Av. MIGUEL SUTIL 6559<br />
                CUIABA, 78048-000<br />
                Brazil
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Section */}
      <section id="privacy" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
            Privacy Policy
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h3>
                <p className="text-gray-600 leading-relaxed">
                  FIAGRIL LTDA is committed to protecting your privacy. We collect minimal information necessary to provide our VPN services. This may include your email address for account creation and payment information for subscription processing. We do not log your browsing activity, connection timestamps, or IP addresses while using our VPN service.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h3>
                <p className="text-gray-600 leading-relaxed">
                  The information we collect is used solely to provide, maintain, and improve our VPN services. We use your email address to communicate important service updates and respond to your inquiries. Payment information is processed securely through third-party payment processors and is never stored on our servers.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">3. No-Log Policy</h3>
                <p className="text-gray-600 leading-relaxed">
                  We maintain a strict no-log policy. We do not track, collect, or store any information about your online activities while connected to our VPN. This includes websites visited, data transferred, DNS queries, or connection timestamps. Your privacy is our top priority.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h3>
                <p className="text-gray-600 leading-relaxed">
                  We implement industry-standard security measures to protect your personal information. All data transmitted through our VPN is encrypted using military-grade AES-256 encryption. We regularly update our security protocols to ensure the highest level of protection for our users.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h3>
                <p className="text-gray-600 leading-relaxed">
                  We may use third-party services for payment processing and customer support. These services are carefully selected and required to maintain the same level of privacy and security standards that we uphold. We do not share your personal information with third parties for marketing purposes.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h3>
                <p className="text-gray-600 leading-relaxed">
                  You have the right to access, modify, or delete your personal information at any time. You can also request a copy of the data we hold about you. To exercise these rights, please contact us at contact@fiagrilltda.com.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">7. Changes to This Policy</h3>
                <p className="text-gray-600 leading-relaxed">
                  We may update this privacy policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any significant changes by email or through our service. Your continued use of our VPN service after such changes constitutes acceptance of the updated policy.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h3>
                <p className="text-gray-600 leading-relaxed">
                  If you have any questions or concerns about this privacy policy or our data practices, please contact us at:
                </p>
                <div className="mt-4 p-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-900 font-semibold mb-2">FIAGRIL LTDA</p>
                  <p className="text-gray-600">Email: contact@fiagrilltda.com</p>
                  <p className="text-gray-600">Phone: +1 531 228 4772</p>
                  <p className="text-gray-600">Address: Av. MIGUEL SUTIL 6559 ANEXO AREA A, CUIABA, 78048-000, Brazil</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/0e54bcbb-1859-4913-b1dd-2eb5d08795ab.png" 
                  alt="FIAGRIL LTDA Logo" 
                  className="h-10 w-10 object-contain"
                />
                <h3 className="text-xl font-bold">FIAGRIL LTDA</h3>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Your trusted partner for secure and private internet access. Protecting your digital freedom with advanced VPN technology.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Quick Links</h4>
              <ul className="space-y-3">
                {[
                  { id: 'home', label: 'Home' },
                  { id: 'about', label: 'About' },
                  { id: 'vpn-service', label: 'VPN Service' },
                  { id: 'contact', label: 'Contact' }
                ].map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-mail-line text-teal-400 mt-1 mr-3"></i>
                  <a href="mailto:contact@fiagrilltda.com" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                    contact@fiagrilltda.com
                  </a>
                </li>
                <li className="flex items-start">
                  <i className="ri-phone-line text-teal-400 mt-1 mr-3"></i>
                  <a href="tel:+15312284772" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                    +1 531 228 4772
                  </a>
                </li>
                <li className="flex items-start">
                  <i className="ri-map-pin-line text-teal-400 mt-1 mr-3"></i>
                  <span className="text-gray-400">
                    Av. MIGUEL SUTIL 6559 ANEXO AREA A<br />
                    CUIABA, 78048-000<br />
                    Brazil
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-700 text-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} FIAGRIL LTDA. All rights reserved. | Brazil, 78048-000 | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
